import { Fragment, useEffect, useMemo, useState } from "react";
import { leadsStats, feConversionMonthly, nutritionConsultMonthly, listIncentiveLocks, lockIncentiveMonth, unlockIncentiveMonth } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lock, Unlock, Download } from "lucide-react";
import { numberLocale } from "@/lib/money";

const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);
const pct = (n) => {
  const v = (n || 0) * 100;
  return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}%`;
};
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Incentives() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [feConv, setFeConv] = useState(null);
  const [nutrition, setNutrition] = useState(null);       // Nutrition Consult monthly payout data
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [scope, setScope] = useState("month"); // "month" | "all"
  const [tab, setTab] = useState("fe_sales"); // "fe_sales" | "fe_conversion" | "lead_nurture"
  const [locks, setLocks] = useState([]);
  const [locking, setLocking] = useState(false);
  const [open, setOpen] = useState({}); // expanded rows, keyed `${tab}:${name}` (CR-0036)
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  const canLock = atLeast(user?.role, "manager");
  const canUnlock = atLeast(user?.role, "admin");
  const monthKey = `${year}-${String(month).padStart(2, "0")}`;
  const currentLock = locks.find((l) => l.month === monthKey) || null;

  const refreshLocks = () => listIncentiveLocks().then(setLocks).catch(() => {});

  useEffect(() => {
    const params = scope === "month" ? { year, month } : {};
    leadsStats(params).then(setStats);
    feConversionMonthly(year, month).then(setFeConv).catch(() => {});
    // Nutrition Consult report is always month-scoped (flat rate × completed consults in month).
    nutritionConsultMonthly(year, month).then(setNutrition).catch(() => {});
  }, [year, month, scope]);
  useEffect(() => { refreshLocks(); }, []);

  const shift = (n) => {
    let y = year, m = month + n;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  const doLock = async () => {
    if (!window.confirm(`Confirm & lock ${MONTH_NAMES[month - 1]} ${year}? After locking, only admins can change payment dates or programme-start (nutrition consult) dates in this month.`)) return;
    setLocking(true);
    try {
      await lockIncentiveMonth(monthKey);
      toast.success(`Locked ${MONTH_NAMES[month - 1]} ${year}`);
      refreshLocks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to lock month");
    } finally {
      setLocking(false);
    }
  };

  const doUnlock = async () => {
    if (!window.confirm(`Unlock ${MONTH_NAMES[month - 1]} ${year}?`)) return;
    setLocking(true);
    try {
      await unlockIncentiveMonth(monthKey);
      toast.success(`Unlocked ${MONTH_NAMES[month - 1]} ${year}`);
      refreshLocks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unlock month");
    } finally {
      setLocking(false);
    }
  };

  if (!stats) return <div className="text-sm text-zinc-500">Loading…</div>;
  const canEditSlabs = atLeast(user?.role, "master_admin");

  const TAB_META = [
    { id: "fe_sales", label: "FE Sales incentives", testid: "tab-fe-sales" },
    { id: "fe_conversion", label: "FE Retention incentives", testid: "tab-fe-conversion" },
    { id: "lead_nurture", label: "Lead Nurture incentives", testid: "tab-lead-nurture" },
    { id: "nutrition", label: "Nutrition Consult", testid: "tab-nutrition-consult" },
  ];

  return (
    <div className="space-y-8" data-testid="incentives-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Commission · calendar month</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Incentives</h2>
          <p className="text-sm text-zinc-400 mt-1">
            Three tracks: <span className="text-zinc-300">FE Sales</span> (per programme-started payments), <span className="text-zinc-300">FE Retention</span> (per FE Conversion sales), <span className="text-zinc-300">Lead Nurture</span>.
            {canEditSlabs && <> <Link data-testid="incentives-link-98" to="/settings" className="text-blue-400 hover:text-blue-300">Edit slabs →</Link></>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tab === "fe_sales" && (
            <div className="inline-flex rounded-full border border-[#262626] bg-[#0a0a0a] p-0.5 text-xs" data-testid="incentives-scope">
              <button onClick={() => setScope("month")} className={`px-3 py-1 rounded-full transition-colors ${scope === "month" ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="scope-month">Month</button>
              <button onClick={() => setScope("all")} className={`px-3 py-1 rounded-full transition-colors ${scope === "all" ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="scope-all">All-time</button>
            </div>
          )}
          {(tab !== "fe_sales" || scope === "month") && (
            <div className="inline-flex items-center gap-1 rounded-full border border-[#262626] bg-[#0a0a0a] px-1 py-1">
              <button onClick={() => shift(-1)} className="p-1 rounded-full hover:bg-[#1a1a1a]" data-testid="incentives-prev-month" aria-label="Previous month"><ChevronLeft className="h-3.5 w-3.5 text-zinc-400" /></button>
              <div className="text-xs text-zinc-200 min-w-[6.5rem] text-center font-medium" data-testid="incentives-month-label">
                {currentLock && <Lock className="inline-block h-3 w-3 text-amber-300 mr-1 -mt-0.5" data-testid="incentives-lock-icon" />}
                {MONTH_NAMES[month - 1]} {year}
              </div>
              <button onClick={() => shift(1)} className="p-1 rounded-full hover:bg-[#1a1a1a]" data-testid="incentives-next-month" aria-label="Next month"><ChevronRight className="h-3.5 w-3.5 text-zinc-400" /></button>
            </div>
          )}
          {(tab !== "fe_sales" || scope === "month") && canLock && !currentLock && (
            <button
              onClick={doLock}
              disabled={locking}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 text-xs px-3 py-1.5 font-medium transition-colors disabled:opacity-50"
              data-testid="incentives-lock-month-btn"
            >
              <Lock className="h-3.5 w-3.5" /> Confirm & Lock
            </button>
          )}
          {(tab !== "fe_sales" || scope === "month") && currentLock && canUnlock && (
            <button
              onClick={doUnlock}
              disabled={locking}
              className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 text-xs px-3 py-1.5 font-medium transition-colors disabled:opacity-50"
              data-testid="incentives-unlock-month-btn"
            >
              <Unlock className="h-3.5 w-3.5" /> Unlock
            </button>
          )}
        </div>
      </div>

      {currentLock && (tab !== "fe_sales" || scope === "month") && (
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs text-amber-200" data-testid="incentives-lock-banner">
          <Lock className="h-3 w-3" />
          {MONTH_NAMES[month - 1]} {year} is locked · confirmed on {new Date(currentLock.locked_at).toLocaleDateString()} by {currentLock.locked_by_name || currentLock.locked_by_email || "—"}
          {!canUnlock && <span className="text-amber-300/70">· only admins can unlock</span>}
        </div>
      )}

      {/* Tab strip */}
      <div className="border-b border-[#1f1f1f] flex gap-6 overflow-x-auto whitespace-nowrap no-scrollbar" data-testid="incentives-tabs">
        {TAB_META.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={t.testid}
            className={`shrink-0 pb-3 -mb-px text-sm font-medium transition-colors ${tab === t.id ? "text-blue-300 border-b-2 border-blue-500" : "text-zinc-400 hover:text-zinc-200"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <LockedMonthsOverview locks={locks} year={year} month={month} onJump={(y, m) => { setYear(y); setMonth(m); }} />

      {tab === "fe_sales" && stats.reps?.length > 0 && (
        <section className="card-surface p-6" data-testid="sales-incentives-table">
          <div className="eyebrow">FE Sales incentives</div>
          <h3 className="font-display text-xl font-bold mt-1 mb-2">Rep performance</h3>
          <p className="text-xs text-zinc-500 mb-4">A sale counts in the <span className="text-zinc-300">month its programme started</span>: the day the nutrition consult was marked done on Accountability (programmes without a nutrition consult: the start date). Ticking &ldquo;Programme started&rdquo; by hand doesn&apos;t start a 6 Week Challenge. Money paid up to the end of that month counts there; a balance paid after that month counts in the month it&apos;s paid. Refunds come off the start month, or off the month they&apos;re given if the start month has passed.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="w-8 px-1 py-2"></th>
                  <th className="px-3 py-2 font-medium">Rep</th>
                  <th className="px-3 py-2 font-medium text-right">Consults</th>
                  <th className="px-3 py-2 font-medium text-right">Sold</th>
                  <th className="px-3 py-2 font-medium text-right">Conversion</th>
                  <th className="px-3 py-2 font-medium text-right">Revenue</th>
                  <th className="px-3 py-2 font-medium text-right">Clients</th>
                  <th className="px-3 py-2 font-medium text-right">Incentive base</th>
                  <th className="px-3 py-2 font-medium text-right">Slab</th>
                  <th className="px-3 py-2 font-medium text-right">Incentive</th>
                </tr>
              </thead>
              <tbody>
                {(stats.reps || []).map((r) => (
                  <Fragment key={r.name}>
                  <tr className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] cursor-pointer" onClick={() => toggle(`fes:${r.name}`)}>
                    <ExpandCell isOpen={!!open[`fes:${r.name}`]} testid={`inc-fes-toggle-${r.name}`} />
                    <td className="px-3 py-2.5 font-medium">{r.name}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-300">{r.consults}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-300">{r.sold}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`text-xs font-mono ${r.conversion >= 0.4 ? "text-emerald-400" : r.conversion >= 0.2 ? "text-amber-400" : "text-rose-400"}`}>{pct(r.conversion)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.revenue)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-300">
                      <span className={r.started_count > 0 ? "text-emerald-300" : "text-zinc-500"}>{r.started_count || 0}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.incentive_base || 0)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-zinc-400">{pct(r.incentive_rate)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-400 font-semibold">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.incentive_amount)}</td>
                  </tr>
                  {open[`fes:${r.name}`] && (
                    <Breakdown colSpan={9} title="Money counted before GST, less gateway fees, refunds and blood tests (by consult date), and the incentive on each" totalLabel="Incentive base" total={r.incentive_base}
                      empty="No started programmes counted for this period."
                      items={(r.items || []).map((m) => {
                        const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
                        const notes = [m.refunded && `${cur}${fmtINR(m.refunded)} refunded`, m.fees && `${cur}${fmtINR(m.fees)} gateway fees`,
                          m.blood_test && `${cur}${fmtINR(m.blood_test)} blood tests`].filter(Boolean);
                        return { key: m.id, name: notes.length ? `${m.name} (after ${notes.join(", ")})` : m.name, date: m.date, programme: m.programme,
                                 price: m.price_ex_tax, amount: m.amount - (m.blood_test || 0), incentive: m.incentive };
                      })} />
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "fe_sales" && stats.reps?.length === 0 && (
        <div className="text-sm text-zinc-500 py-8 text-center card-surface p-6">No FE Sales activity for this period.</div>
      )}

      {tab === "fe_conversion" && (
        <section className="card-surface p-6" data-testid="fe-conversion-incentives-table">
          <div className="eyebrow">FE Retention incentives (previously FE Conversion)</div>
          <h3 className="font-display text-xl font-bold mt-1 mb-2">Per Accountability Coach · {MONTH_NAMES[month - 1]} {year}</h3>
          <p className="text-xs text-zinc-500 mb-4">A front-end member moving onto 12 Week / 24 Week / Annual, entered in All Sales with status <span className="text-zinc-300">FE Retention</span>. Paid to the <span className="text-zinc-300">Accountability Coach</span> on the sale. Base = <span className="text-zinc-300">net received</span> (after GST and fees); paid at the rate of the slab the coach's month falls in, on the whole amount. New Consult sales don&apos;t count. See <Link data-testid="incentives-link-217" to="/sales/fe-conversion" className="text-blue-400 hover:text-blue-300">full report →</Link></p>
          {feConv && feConv.slabs?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
              {(feConv.slabs || []).map((s, i) => {
                const noCap = s.max === null || s.max === undefined || s.max >= 10_000_000;
                const fmt = (v) => (v >= 100000 ? `${(v / 100000).toFixed(v % 100000 ? 1 : 0)}L` : `${(v / 1000).toFixed(0)}K`);
                return (
                  <div key={`${s.min}-${s.max ?? "inf"}-${s.rate}`} className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-center">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/80">
                      {noCap ? `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmt(s.min)}+` : `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmt(s.min)} – ${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmt(s.max)}`}
                    </div>
                    <div className="text-xl font-bold text-emerald-300 tabular-nums mt-0.5">{(s.rate * 100).toFixed(0)}%</div>
                  </div>
                );
              })}
            </div>
          )}
          {!feConv || feConv.reps.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No FE Retention sales for this month.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                    <th className="w-8 px-1 py-2"></th>
                  <th className="px-3 py-2 font-medium">Accountability Coach</th>
                    <th className="px-3 py-2 font-medium text-right">Conversions</th>
                    <th className="px-3 py-2 font-medium text-right">Net revenue</th>
                    <th className="px-3 py-2 font-medium text-right" title="The slab the month's net received falls in">Slab rate</th>
                    <th className="px-3 py-2 font-medium text-right">Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {(feConv.reps || []).map((r) => (
                    <Fragment key={r.name}>
                    <tr className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] cursor-pointer" data-testid={`inc-fec-${r.name}`} onClick={() => toggle(`fec:${r.name}`)}>
                      <ExpandCell isOpen={!!open[`fec:${r.name}`]} testid={`inc-fec-toggle-${r.name}`} />
                      <td className="px-3 py-2.5 font-medium">{r.name}</td>
                      <td className="px-3 py-2.5 text-right text-zinc-300 tabular-nums">{r.conversions}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.revenue)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-zinc-400">{(r.slab_rate * 100).toFixed(1)}%</td>
                      <td className="px-3 py-2.5 text-right font-mono text-amber-300 font-semibold">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.payout)}</td>
                    </tr>
                    {open[`fec:${r.name}`] && (
                      <Breakdown colSpan={6} title="Included sales (net received)" totalLabel="Net revenue" total={r.revenue}
                        items={(r.members || []).map((m) => ({ key: m.id, name: m.name, date: m.date, programme: m.programme, by: m.sales_rep, amount: m.net_received }))} />
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === "lead_nurture" && stats.lead_ex_rows?.length > 0 && (
        <section className="card-surface p-6" data-testid="lead-nurture-incentives-table">
          <div className="eyebrow">Lead Nurture Executive incentives</div>
          <h3 className="font-display text-xl font-bold mt-1 mb-4">From generated leads</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="w-8 px-1 py-2"></th>
                  <th className="px-3 py-2 font-medium">Lead Nurture Executive</th>
                  <th className="px-3 py-2 font-medium text-right">Leads</th>
                  <th className="px-3 py-2 font-medium text-right">Converted</th>
                  <th className="px-3 py-2 font-medium text-right">Revenue</th>
                  <th className="px-3 py-2 font-medium text-right">Slab</th>
                  <th className="px-3 py-2 font-medium text-right">Incentive</th>
                </tr>
              </thead>
              <tbody>
                {(stats.lead_ex_rows || []).map((r) => (
                  <Fragment key={r.name}>
                  <tr className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] cursor-pointer" onClick={() => toggle(`ln:${r.name}`)}>
                    <ExpandCell isOpen={!!open[`ln:${r.name}`]} testid={`inc-ln-toggle-${r.name}`} />
                    <td className="px-3 py-2.5 font-medium">{r.name}</td>
                    <td className="px-3 py-2.5 text-right">{r.leads}</td>
                    <td className="px-3 py-2.5 text-right">{r.converted}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.revenue)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-zinc-400">{pct(r.incentive_rate)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-400 font-semibold">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.incentive_amount)}</td>
                  </tr>
                  {open[`ln:${r.name}`] && (
                    <Breakdown colSpan={7} title="Converted leads (sale net)" totalLabel="Revenue" total={r.revenue}
                      empty="None of this period's leads converted yet."
                      items={(r.items || []).map((m) => ({ key: m.id, name: m.name, date: m.date, programme: m.programme, by: m.sales_ex, amount: m.amount }))} />
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "lead_nurture" && (!stats.lead_ex_rows || stats.lead_ex_rows.length === 0) && (
        <div className="text-sm text-zinc-500 py-8 text-center card-surface p-6">No Lead Nurture activity for this period.</div>
      )}

      {/* Nutrition Consult — flat rate × completed consults tied to Front End leads this month. */}
      {tab === "nutrition" && (
        <section className="card-surface p-6" data-testid="nutrition-consult-incentives-table">
          <div className="eyebrow">Nutrition Consult incentives</div>
          <h3 className="font-display text-xl font-bold mt-1 mb-2">Per Nutrition Coach · {MONTH_NAMES[month - 1]} {year}</h3>
          <p className="text-xs text-zinc-500 mb-4">
            Applies to every Front End consult marked as <span className="text-zinc-300">Nutrition Consult · Done</span> in Accountability,
            paid with the payroll for the <span className="text-zinc-300">month the member&apos;s programme ends</span> (its end date, which pauses move; without one, six weeks from the nutrition consult).
            Payout = <span className="text-zinc-300 font-mono">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(nutrition?.rate || 0)}</span> × consults whose programme ends this month. The rate is set per country in Global Admin → Incentives.
          </p>
          {(!nutrition || nutrition.reps?.length === 0) ? (
            <div className="text-sm text-zinc-500 py-8 text-center">
              {nutrition && nutrition.rate === 0
                ? "Rate is 0 — set a flat rate in Settings to activate this incentive."
                : "No completed nutrition consults for this month yet."}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/80">Rate / consult</div>
                  <div className="text-xl font-bold text-emerald-300 tabular-nums mt-0.5">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(nutrition.rate)}</div>
                </div>
                <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-blue-300/80">Consults done</div>
                  <div className="text-xl font-bold text-blue-300 tabular-nums mt-0.5">{nutrition.total_consults}</div>
                </div>
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/80">Total payout</div>
                  <div className="text-xl font-bold text-amber-300 tabular-nums mt-0.5">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(nutrition.total_payout)}</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                      <th className="w-8 px-1 py-2"></th>
                  <th className="px-3 py-2 font-medium">Nutrition Coach</th>
                      <th className="px-3 py-2 font-medium text-right">Consults</th>
                      <th className="px-3 py-2 font-medium text-right">Rate</th>
                      <th className="px-3 py-2 font-medium text-right">Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(nutrition.reps || []).map((r) => (
                      <Fragment key={r.coach}>
                      <tr className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] cursor-pointer" data-testid={`inc-nutrition-${r.coach}`} onClick={() => toggle(`nc:${r.coach}`)}>
                        <ExpandCell isOpen={!!open[`nc:${r.coach}`]} testid={`inc-nc-toggle-${r.coach}`} />
                        <td className="px-3 py-2.5 font-medium">{r.coach}</td>
                        <td className="px-3 py-2.5 text-right text-zinc-300 tabular-nums">{r.count}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-zinc-400">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(nutrition.rate)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-300 font-semibold">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.payout)}</td>
                      </tr>
                      {open[`nc:${r.coach}`] && (
                        <Breakdown colSpan={5} title="Consults counted (Nutrition Consult · Done)" totalLabel="Payout" total={r.payout}
                          items={(r.members || []).map((m, i) => ({ key: m.id || `${m.member}-${i}`, name: m.member, date: m.date, programme: m.programme, amount: m.amount ?? nutrition.rate }))} />
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      {tab === "fe_sales" && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="slab-reference">
          <SlabCard title="FE Sales slabs" rows={stats.sales_slabs} />
          <SlabCard title="Lead Nurture slabs" rows={stats.leads_slabs} />
        </section>
      )}
    </div>
  );
}

// Chevron cell for an expandable person row (CR-0036). The whole row toggles; this is the affordance.
function ExpandCell({ isOpen, testid }) {
  return (
    <td className="w-8 px-1 py-2.5 align-middle">
      <span className="inline-flex p-1 rounded hover:bg-[#1a1a1a]" role="button" aria-label={isOpen ? "Hide clients" : "Show clients"} aria-expanded={isOpen} data-testid={testid}>
        <ChevronRight className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
      </span>
    </td>
  );
}

// Clients behind one person's incentive. Items come from the same backend calculation as the row,
// so the total here matches the number in the row.
function Breakdown({ colSpan, title, items, total, totalLabel, empty }) {
  const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    return isNaN(dt) ? String(d).slice(0, 10) : dt.toLocaleDateString();
  };
  return (
    <tr className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
      <td colSpan={colSpan} className="px-3 py-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{title}</div>
        {items.length === 0 ? (
          <div className="text-xs text-zinc-500">{empty || "Nothing counted for this period."}</div>
        ) : (
          <div className="space-y-1 max-w-[calc(100vw-5rem)] md:max-w-none">
            {items.map((m) => (
              <div key={m.key} className="flex items-start justify-between gap-3 text-xs bg-[#111] rounded-lg px-3 py-1.5 border border-[#1a1a1a]">
                <div className="min-w-0 whitespace-normal">
                  <span className="text-zinc-100 font-medium">{m.name || "—"}</span>
                  {m.date && <span className="text-zinc-500 ml-2">· {fmtDate(m.date)}</span>}
                  {m.programme && <span className="text-zinc-500 ml-2">· {m.programme}</span>}
                  {m.price != null && <span className="text-zinc-500 ml-2">· price <span className="text-zinc-300">{cur}{fmtINR(m.price)}</span> before GST</span>}
                  {m.by && <span className="text-zinc-500 ml-2">· sold by <span className="text-zinc-400">{m.by}</span></span>}
                </div>
                <div className="shrink-0 text-right">
                  <div className={`font-mono ${m.amount < 0 ? "text-rose-300" : "text-emerald-300"}`}>{cur}{fmtINR(m.amount)}</div>
                  {m.incentive != null && <div className="font-mono text-[11px] text-zinc-400">incentive {cur}{fmtINR(m.incentive)}</div>}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between text-xs px-3 pt-1.5">
              <span className="text-zinc-500">{items.length} client{items.length === 1 ? "" : "s"} · {totalLabel}</span>
              <span className="font-mono text-zinc-200 font-semibold">{cur}{fmtINR(total)}</span>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

function SlabCard({ title, rows }) {
  const fmt = (n) => n == null ? "∞" : (n >= 100000 ? `${(n/100000).toFixed(n%100000===0?0:1)}L` : `${n/1000}K`);
  return (
    <div className="card-surface p-5">
      <div className="eyebrow">{title}</div>
      <table className="w-full text-sm mt-3">
        <tbody>
          {(rows || []).map((r, i) => (
            // Same slab-range-based key as the FE Sales panel — stable across edits.
            <tr key={`${r.min}-${r.max ?? "inf"}-${r.rate}-${i}`} className="border-b border-[#1a1a1a] last:border-0">
              <td className="py-2 text-zinc-400 font-mono text-xs">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmt(r.min)} – {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmt(r.max)}</td>
              <td className="py-2 text-right font-display font-bold text-emerald-400 tabular-nums">{(r.rate*100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LockedMonthsOverview({ locks, year, month, onJump }) {
  if (!locks || locks.length === 0) return null;
  const currentMonthKey = `${year}-${String(month).padStart(2, "0")}`;

  const fmtMonth = (m) => {
    const [y, mm] = m.split("-");
    return `${MONTH_NAMES[parseInt(mm, 10) - 1]} ${y}`;
  };

  const exportCsv = () => {
    const headers = ["month", "locked_at", "locked_by_name", "locked_by_email", "note"];
    const rows = (locks || []).map((l) => headers.map((h) => {
      const v = l[h] ?? "";
      const s = String(v).replace(/"/g, '""');
      return /[,"\n]/.test(s) ? `"${s}"` : s;
    }).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `locked_incentive_months_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section className="card-surface p-5" data-testid="locked-months-overview">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 text-amber-300" />
          <div className="eyebrow">Confirmed incentive months</div>
          <span className="text-[11px] text-zinc-500">({locks.length})</span>
        </div>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#3a3a3a] bg-[#0a0a0a] hover:bg-[#141414] text-zinc-200 text-xs px-3 py-1.5 font-medium transition-colors"
          data-testid="locked-months-export"
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="locked-months-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-3 py-2 font-medium">Month</th>
              <th className="px-3 py-2 font-medium">Confirmed by</th>
              <th className="px-3 py-2 font-medium">Confirmed on</th>
              <th className="px-3 py-2 font-medium">Note</th>
              <th className="px-3 py-2 font-medium text-right"></th>
            </tr>
          </thead>
          <tbody>
            {(locks || []).map((l) => {
              const isCurrent = l.month === currentMonthKey;
              const [y, mm] = l.month.split("-");
              return (
                <tr key={l.id || l.month} className={`border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] ${isCurrent ? "bg-amber-500/5" : ""}`} data-testid={`locked-months-row-${l.month}`}>
                  <td className="px-3 py-2.5 font-mono text-amber-200">
                    <span className="inline-flex items-center gap-1.5">
                      <Lock className="h-3 w-3" />
                      {fmtMonth(l.month)}
                      {isCurrent && <span className="text-[10px] uppercase tracking-[0.15em] text-amber-400 ml-1">viewing</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="text-zinc-200">{l.locked_by_name || "—"}</div>
                    <div className="text-[10px] text-zinc-500">{l.locked_by_email}</div>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-400 font-mono">{new Date(l.locked_at).toLocaleString([], { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-3 py-2.5 text-zinc-500 text-xs italic">{l.note || "—"}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => onJump(parseInt(y, 10), parseInt(mm, 10))}
                      className="text-[11px] text-blue-300 hover:text-blue-200 border border-blue-500/30 hover:border-blue-500/60 rounded-full px-2.5 py-1 hover:bg-blue-500/10 transition-colors"
                      data-testid={`locked-months-jump-${l.month}`}
                    >
                      View month
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
